import { TooltipPortal } from "@radix-ui/react-tooltip";
import React from "react";

import { BasicTooltipProps } from "./types";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from ".";

import { useCheckOverflow } from "@/hooks/useCheckOverflow";

export enum OverflowDirection {
  HORIZONTAL = "horizontal",
  VERTICAL = "vertical",
}

export interface OverflowDisplayTooltipProps extends BasicTooltipProps {
  overflowDirection?: OverflowDirection;
  lineClamp?: number;
  styles?: React.CSSProperties;
}

export const OverflowDisplayTooltip = ({
  content,
  children,
  variant = "black",
  side = "top",
  usePortal = true,
  delayDuration,
  className,
  ignoreOverflow = false,
  overflowDirection = OverflowDirection.HORIZONTAL,
  lineClamp = 1,
  shouldShow = true,
  styles,
}: OverflowDisplayTooltipProps) => {
  const { ref, isOverflowing } = useCheckOverflow({});

  const renderContent = () =>
    usePortal ? (
      <TooltipPortal>
        <TooltipContent side={side} variant={variant} style={{ zIndex: 9999 }}>
          {content}
        </TooltipContent>
      </TooltipPortal>
    ) : (
      <TooltipContent side={side} variant={variant} style={{ zIndex: 9999 }}>
        {content}
      </TooltipContent>
    );

  const getOverflowClasses = () => {
    const baseClasses = "block overflow-hidden";

    switch (overflowDirection) {
      case OverflowDirection.HORIZONTAL:
        return `${baseClasses} truncate`;
      case OverflowDirection.VERTICAL:
        return `${baseClasses}`;
      default:
        return `${baseClasses} truncate`;
    }
  };

  const getOverflowStyles = (): React.CSSProperties => {
    if (overflowDirection === OverflowDirection.VERTICAL && lineClamp) {
      return {
        display: "-webkit-box",
        WebkitLineClamp: lineClamp,
        WebkitBoxOrient: "vertical",
        ...styles,
      };
    }
    return styles || {};
  };

  // Handle both React element children and text/primitive children
  const isReactElement = React.isValidElement(children);

  const combinedStyles = getOverflowStyles();

  const childWithRef = isReactElement
    ? React.cloneElement(children as React.ReactElement, {
        ref,
        className: `${(children as React.ReactElement).props.className || ""} ${
          className || ""
        }`.trim(),
        style: {
          ...(children as React.ReactElement).props.style,
          ...combinedStyles,
        },
      })
    : React.createElement(
        "span",
        {
          ref,
          className: `${className || ""}`.trim(),
          style: combinedStyles,
        },
        children,
      );

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip>
        <TooltipTrigger className={getOverflowClasses()} asChild>
          {childWithRef}
        </TooltipTrigger>
        {(isOverflowing || ignoreOverflow) && shouldShow && renderContent()}
      </Tooltip>
    </TooltipProvider>
  );
};
